import React, { useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Mail, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RegisterPage = ({ onLogin }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const roleParam = searchParams.get('role');

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    role: roleParam || 'customer'
  });
  const [loading, setLoading] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState(null);
  const [resending, setResending] = useState(false);

  const handleResendVerification = async () => {
    setResending(true);
    try {
      await axios.post(`${API}/auth/resend-verification`, { email: registeredEmail });
      toast.success('Bestätigungs-E-Mail wurde erneut gesendet');
    } catch (error) {
      toast.error('Fehler beim erneuten Senden. Bitte versuche es später erneut.');
    } finally {
      setResending(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwörter stimmen nicht überein');
      return;
    }

    if (formData.password.length < 8) {
      toast.error('Das Passwort muss mindestens 8 Zeichen lang sein');
      return;
    }

    if (!/[a-zA-Z]/.test(formData.password)) {
      toast.error('Das Passwort muss mindestens einen Buchstaben enthalten');
      return;
    }

    if (!/[0-9]/.test(formData.password)) {
      toast.error('Das Passwort muss mindestens eine Zahl enthalten');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/auth/register`, {
        email: formData.email,
        password: formData.password,
        role: formData.role
      });

      setRegisteredEmail(formData.email);
    } catch (error) {
      console.error('Registration error:', error);
      toast.error(error.response?.data?.detail || 'Registrierung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  if (registeredEmail) {
    return (
      <div className="min-h-screen bg-gradient-to-br bg-[#F4F2F2] flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-3xl shadow-xl p-8 text-center">
            <div className="w-16 h-16 bg-[#FFF0D1] rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="w-8 h-8 text-[#2B2142]" />
            </div>
            <h1 className="text-2xl font-bold mb-2 text-gray-900">E-Mail bestätigen</h1>
            <p className="text-gray-600 mb-2">
              Wir haben eine Bestätigungs-E-Mail an
            </p>
            <p className="font-semibold text-gray-900 mb-6">{registeredEmail}</p>
            <p className="text-gray-600 mb-8">
              Bitte klicke auf den Link in der E-Mail, um dein Konto zu aktivieren.
            </p>

            <button
              onClick={handleResendVerification}
              disabled={resending}
              className="flex items-center gap-2 mx-auto px-6 py-3 border-2 border-gray-200 rounded-xl hover:border-[#2B2142] text-gray-700 hover:text-[#2B2142] font-semibold transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${resending ? 'animate-spin' : ''}`} />
              {resending ? 'Wird gesendet...' : 'E-Mail erneut senden'}
            </button>

            <div className="mt-6">
              <button
                onClick={() => navigate('/login', { state: location.state })}
                className="text-[#2B2142] font-semibold hover:underline"
              >
                Zur Anmeldung
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br bg-[#F4F2F2] flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-gray-600 hover:text-[#2B2142] mb-8"
          data-testid="back-button"
        >
          <ArrowLeft className="w-5 h-5" />
          Zurück
        </button>

        <div className="bg-white rounded-3xl shadow-xl p-8">
          <h1 className="text-3xl font-bold mb-2 text-gray-900">Konto erstellen</h1>
          <p className="text-gray-600 mb-8">Werde Teil von EventConnect</p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Ich bin...
              </label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  data-testid="role-customer-btn"
                  onClick={() => setFormData({ ...formData, role: 'customer' })}
                  className={`p-4 rounded-xl border-2 font-semibold ${
                    formData.role === 'customer'
                      ? 'border-[#2B2142] bg-[#FFF0D1]/50 text-[#2B2142]'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  Kunde
                </button>
                <button
                  type="button"
                  data-testid="role-provider-btn"
                  onClick={() => setFormData({ ...formData, role: 'provider' })}
                  className={`p-4 rounded-xl border-2 font-semibold ${
                    formData.role === 'provider'
                      ? 'border-[#2B2142] bg-[#FFF0D1]/50 text-[#2B2142]'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  Dienstleister
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                E-Mail
              </label>
              <input
                type="email"
                data-testid="email-input"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#2B2142] focus:outline-none"
                placeholder="deine@email.de"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Passwort
              </label>
              <input
                type="password"
                data-testid="password-input"
                required
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#2B2142] focus:outline-none"
                placeholder="Mind. 8 Zeichen, Buchstaben + Zahlen"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Passwort bestätigen
              </label>
              <input
                type="password"
                data-testid="confirm-password-input"
                required
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#2B2142] focus:outline-none"
                placeholder="Passwort wiederholen"
              />
            </div>

            <button
              type="submit"
              data-testid="register-submit-btn"
              disabled={loading}
              className="w-full py-4 bg-[#2B2142] hover:bg-[#1E1730] text-white font-semibold rounded-xl disabled:opacity-50"
            >
              {loading ? 'Wird erstellt...' : 'Konto erstellen'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-600">
              Hast du bereits ein Konto?{' '}
              <button
                onClick={() => navigate('/login', { state: location.state })}
                className="text-[#2B2142] font-semibold hover:underline"
                data-testid="go-to-login"
              >
                Jetzt anmelden
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
